import { Link } from '@tanstack/react-router';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerBody, DrawerFooter } from '../../../components/ui/Drawer';
import { Button } from '../../../components/ui/Button';
import { Skeleton } from '../../../components/ui/Skeleton';
import { Money } from '../../../components/ui/Money';
import { StatusBadge } from '../../../components/ui/StatusBadge';
import { useToast } from '../../../components/ui/Toast';
import { generalTransactionsHooks } from '../network/general-transactions.api';

interface GeneralTransactionDetailDrawerProps {
  transactionId: string | null;
  onClose: () => void;
}

const STATUS_META = {
  DRAFT: { status: 'warning' as const, label: 'Pending Approval' },
  POSTED: { status: 'success' as const, label: 'Posted' },
  REVERSED: { status: 'danger' as const, label: 'Reversed' },
};

export function GeneralTransactionDetailDrawer({ transactionId, onClose }: GeneralTransactionDetailDrawerProps) {
  const { data: tx, isLoading, isError } = generalTransactionsHooks.useDetail(transactionId);
  const reverseMutation = generalTransactionsHooks.useReverse(transactionId ?? '');
  const { toast } = useToast();

  const lines = tx?.lines ?? [];
  const totalDebit = lines.filter((l) => l.direction === 'DEBIT').reduce((sum, l) => sum + l.amountPaise, 0);
  const totalCredit = lines.filter((l) => l.direction === 'CREDIT').reduce((sum, l) => sum + l.amountPaise, 0);

  return (
    <Drawer open={!!transactionId} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent className="w-full max-w-xl bg-surface-1">
        <DrawerHeader>
          <DrawerTitle>General Transaction Detail</DrawerTitle>
        </DrawerHeader>
        <DrawerBody>
          {isLoading ? (
            <Skeleton rows={5} />
          ) : isError || !tx ? (
            <p className="text-danger-600">Failed to load transaction details.</p>
          ) : (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-xs text-text-muted block">Type</span>
                  <span className="font-semibold text-text-primary">{tx.type}</span>
                </div>
                <div>
                  <span className="text-xs text-text-muted block">Date</span>
                  <span className="font-semibold text-text-primary">{new Date(tx.entryDate).toLocaleDateString()}</span>
                </div>
                <div>
                  <span className="text-xs text-text-muted block">Status</span>
                  <StatusBadge status={STATUS_META[tx.status].status} label={STATUS_META[tx.status].label} />
                </div>
              </div>

              <div>
                <span className="text-xs text-text-muted block mb-1">Particulars</span>
                <p className="text-sm bg-surface-2 p-3 rounded">{tx.particulars}</p>
              </div>

              <div>
                <h4 className="text-sm font-semibold mb-2">Lines</h4>
                <div className="border border-surface-2 rounded-lg overflow-hidden">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-surface-2 text-text-muted">
                      <tr>
                        <th className="py-2 px-3">Account</th>
                        <th className="py-2 px-3">Association</th>
                        <th className="py-2 px-3 text-right">Debit</th>
                        <th className="py-2 px-3 text-right">Credit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line) => (
                        <tr key={line.id} className="border-b border-surface-2 last:border-0">
                          <td className="py-2 px-3">
                            <div className="font-medium text-text-primary">{line.accountName}</div>
                            <div className="text-xs text-text-muted font-mono">{line.accountCode}</div>
                          </td>
                          <td className="py-2 px-3 text-xs text-text-muted">
                            {line.referenceType ? `${line.referenceType}: ${line.referenceId ?? ''}` : (line.associationType ?? '-')}
                          </td>
                          <td className="py-2 px-3 text-right font-mono">
                            {line.direction === 'DEBIT' ? <Money paise={line.amountPaise} /> : '-'}
                          </td>
                          <td className="py-2 px-3 text-right font-mono">
                            {line.direction === 'CREDIT' ? <Money paise={line.amountPaise} /> : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-surface-2 font-semibold">
                      <tr>
                        <td className="py-2 px-3" colSpan={2}>Total</td>
                        <td className="py-2 px-3 text-right font-mono"><Money paise={totalDebit} /></td>
                        <td className="py-2 px-3 text-right font-mono"><Money paise={totalCredit} /></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {tx.status === 'DRAFT' && (
                <div className="bg-warning-bg text-warning-text text-sm p-3 rounded">
                  This transaction is pending maker-checker approval and has not yet posted to the ledger.{' '}
                  <Link to="/approvals" className="underline font-medium">View Pending Approvals →</Link>
                </div>
              )}
            </div>
          )}
        </DrawerBody>
        <DrawerFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          {!isLoading && tx && tx.status === 'POSTED' && (
            <Button
              variant="danger"
              loading={reverseMutation.isPending}
              onClick={async () => {
                try {
                  await reverseMutation.mutateAsync();
                  toast({ type: 'success', title: 'Reversed', description: 'Transaction reversed successfully.' });
                  onClose();
                } catch (err: unknown) {
                  const error = err as { message?: string };
                  console.error('Failed to reverse general transaction', err);
                  toast({ type: 'error', title: 'Error', description: error.message || 'Failed to reverse transaction.' });
                }
              }}
            >
              Reverse
            </Button>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
